import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import * as programsService from "../services/programs";
import * as programTestsService from "../services/programTests";
import ProgramTestEditor from "./ProgramTestEditor";
import "./Dashboard.css";
import "./Tests.css";
import "./ProgramTestEditor.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeProgramLevels(levels) {
  const raw = Array.isArray(levels) ? levels : [];
  const normalized = [];
  const seen = new Set();

  raw.forEach((item) => {
    if (item == null) return;
    if (typeof item === "string") {
      const grade = String(item).trim();
      if (!grade || seen.has(grade)) return;
      seen.add(grade);
      normalized.push({ grade, label: "" });
      return;
    }

    if (typeof item === "object") {
      const grade = String(item.grade ?? item.id ?? "").trim();
      if (!grade || seen.has(grade)) return;
      seen.add(grade);
      normalized.push({
        grade,
        label: String(item.label ?? item.title ?? "").trim(),
      });
    }
  });

  return normalized;
}

export default function ProgramTestNew() {
  const { user, accessToken, effectiveRole } = useAuth();
  const { t, language } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";

  const isTeacher = effectiveRole === "teacher";
  const isAdmin = ["admin", "superadmin"].includes(effectiveRole);
  const isTeacherOrAdmin = isTeacher || isAdmin;

  const [programs, setPrograms] = useState([]);
  const [programLoading, setProgramLoading] = useState(true);
  const [error, setError] = useState("");

  const [createForm, setCreateForm] = useState({
    programSlug: "",
    levelGrade: "",
    testType: "mixed_test",
    title: "",
    description: "",
    durationMinutes: "",
    isPublished: false,
  });
  const [createBusy, setCreateBusy] = useState(false);
  const [createdTestId, setCreatedTestId] = useState(null);

  const selectedProgram = useMemo(() => {
    if (!createForm.programSlug) return null;
    return programs.find(
      (p) =>
        p?.slug === createForm.programSlug ||
        String(p?.id) === String(createForm.programSlug),
    );
  }, [createForm.programSlug, programs]);

  const levelOptions = useMemo(() => {
    const direct = normalizeProgramLevels(selectedProgram?.levels);
    if (direct.length) return direct;

    const catalog = asArray(
      t("landingPage.levels.items", { returnObjects: true }),
    );
    return catalog
      .map((item) => ({
        grade: String(item?.grade ?? "").trim(),
        label: String(item?.label ?? "").trim(),
      }))
      .filter((lvl) => lvl.grade);
  }, [selectedProgram?.levels, t]);

  useEffect(() => {
    let ignore = false;

    async function loadPrograms() {
      setProgramLoading(true);
      try {
        const payload = await programsService.fetchPrograms({
          accessToken: user ? accessToken : undefined,
        });
        if (ignore) return;
        setPrograms(asArray(payload?.programs));
      } catch {
        if (ignore) return;
        setPrograms([]);
      } finally {
        if (!ignore) setProgramLoading(false);
      }
    }

    loadPrograms();

    return () => {
      ignore = true;
    };
  }, [accessToken, user]);

  if (!isTeacherOrAdmin) {
    return <Navigate to="/placement-test" replace />;
  }

  const doCreate = async (event) => {
    event.preventDefault();
    if (!accessToken) return;

    const payload = {
      program: String(createForm.programSlug || "").trim(),
      level_grade: String(createForm.levelGrade || "").trim(),
      test_type:
        String(createForm.testType || "mixed_test").trim() || "mixed_test",
      title: String(createForm.title || "").trim(),
      description: String(createForm.description || "").trim(),
      is_published: Boolean(createForm.isPublished),
    };

    const minutesRaw = String(createForm.durationMinutes || "").trim();
    if (minutesRaw) {
      const value = Number(minutesRaw);
      if (!Number.isNaN(value)) {
        payload.duration_minutes = value;
      }
    }

    setCreateBusy(true);
    setError("");
    try {
      const created = await programTestsService.createProgramTest({
        accessToken,
        payload,
      });
      if (created?.id) {
        try {
          await programTestsService.createScaffoldExercises({
            accessToken,
            testId: created.id,
            testType: payload.test_type,
            startOrder: 0,
          });
        } catch {
          // best-effort; teachers can still add exercises manually
        }
      }

      setCreatedTestId(created?.id ?? null);
      if (created?.id) {
        try {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
          window.scrollTo(0, 0);
        }
      }
    } catch (e) {
      setError(
        e?.message ||
          t("testsModule.newTest.failedCreate", {
            defaultValue: "Failed to create test",
          }),
      );
    } finally {
      setCreateBusy(false);
    }
  };

  return (
    <main className="test-editor" dir={dir}>
      <div className="container test-editor__container">
        <header className="test-editor__header">
          <div>
            <h1 className="test-editor__title">
              {t("testsModule.dashboard.newTest", { defaultValue: "New test" })}
            </h1>
            <p className="test-editor__subtitle">
              {t("testsModule.newTest.subtitle", {
                defaultValue: "Create a new test, then start adding exercises.",
              })}
            </p>
          </div>

          <div className="test-editor__headerActions">
            <Link to="/tests" className="btn ghost">
              {t("testsModule.common.back", { defaultValue: "Back" })}
            </Link>
          </div>
        </header>

        {error ? <div className="test-editor__error">{error}</div> : null}

        <section className="test-editor__card test-editor__card--transparent">
          <h2 className="test-editor__sectionTitle">
            {t("testsModule.newTest.detailsTitle", {
              defaultValue: "Test details",
            })}
          </h2>

          {!programLoading && !programs.length ? (
            // Teachers can only create tests for programs an admin assigned to them.
            <div className="test-editor__note" role="status">
              <i className="fa-solid fa-circle-info" aria-hidden="true" />{" "}
              {t("testsModule.dashboard.noProgramsAssigned")}
            </div>
          ) : null}

          <form onSubmit={doCreate}>
            <div className="test-editor__grid">
              <label>
                <span>
                  {t("testsModule.common.program", { defaultValue: "Program" })}
                </span>
                <select
                  value={createForm.programSlug}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      programSlug: e.target.value,
                    }))
                  }
                  disabled={createBusy || programLoading}
                  required
                >
                  <option value="">
                    {t("testsModule.common.select", { defaultValue: "Select" })}
                  </option>
                  {programs.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.title || p.slug}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>
                  {t("testsModule.common.level", { defaultValue: "Level" })}
                </span>
                <input
                  value={createForm.levelGrade}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, levelGrade: e.target.value }))
                  }
                  disabled={createBusy}
                  placeholder={t("testsModule.dashboard.placeholderLevel", {
                    defaultValue: "e.g. CP",
                  })}
                  list="test-level-options"
                  required
                />
                <datalist id="test-level-options">
                  {levelOptions.map((lvl) => (
                    <option key={lvl.grade} value={lvl.grade} />
                  ))}
                </datalist>
              </label>

              <label>
                <span>
                  {t("testsModule.editor.testType", {
                    defaultValue: "Test type",
                  })}
                </span>
                <select
                  value={createForm.testType}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, testType: e.target.value }))
                  }
                  disabled={createBusy}
                >
                  <option value="text_comprehension">
                    {t("testsModule.testTypes.text_comprehension", {
                      defaultValue: "Text Comprehension",
                    })}
                  </option>
                  <option value="grammar">
                    {t("testsModule.testTypes.grammar", {
                      defaultValue: "Grammar",
                    })}
                  </option>
                  <option value="vocabulary">
                    {t("testsModule.testTypes.vocabulary", {
                      defaultValue: "Vocabulary",
                    })}
                  </option>
                  <option value="math_problems">
                    {t("testsModule.testTypes.math_problems", {
                      defaultValue: "Math Problems",
                    })}
                  </option>
                  <option value="calculations">
                    {t("testsModule.testTypes.calculations", {
                      defaultValue: "Calculations",
                    })}
                  </option>
                  <option value="qcm">
                    {t("testsModule.testTypes.qcm", { defaultValue: "QCM" })}
                  </option>
                  <option value="problem_solving">
                    {t("testsModule.testTypes.problem_solving", {
                      defaultValue: "Problem Solving",
                    })}
                  </option>
                  <option value="data_interpretation">
                    {t("testsModule.testTypes.data_interpretation", {
                      defaultValue: "Data Interpretation",
                    })}
                  </option>
                  <option value="written_expression">
                    {t("testsModule.testTypes.written_expression", {
                      defaultValue: "Written Expression",
                    })}
                  </option>
                  <option value="mixed_test">
                    {t("testsModule.testTypes.mixed_test", {
                      defaultValue: "Mixed Test",
                    })}
                  </option>
                </select>
              </label>

              <label className="test-editor__full">
                <span>
                  {t("testsModule.common.title", { defaultValue: "Title" })}
                </span>
                <input
                  value={createForm.title}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, title: e.target.value }))
                  }
                  disabled={createBusy}
                  required
                />
              </label>

              <label className="test-editor__full">
                <span>
                  {t("testsModule.common.description", {
                    defaultValue: "Description",
                  })}
                </span>
                <textarea
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      description: e.target.value,
                    }))
                  }
                  disabled={createBusy}
                  rows={3}
                />
              </label>

              <label>
                <span>
                  {t("testsModule.editor.durationMinutes", {
                    defaultValue: "Duration (minutes)",
                  })}
                </span>
                <input
                  value={createForm.durationMinutes}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      durationMinutes: e.target.value,
                    }))
                  }
                  disabled={createBusy}
                  type="number"
                  min="1"
                />
              </label>

              <label className="test-editor__inline">
                <input
                  type="checkbox"
                  checked={Boolean(createForm.isPublished)}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      isPublished: e.target.checked,
                    }))
                  }
                  disabled={createBusy}
                />
                <span>
                  {t("testsModule.editor.published", {
                    defaultValue: "Published",
                  })}
                </span>
              </label>

              <div className="test-editor__actions">
                <button
                  className="btn primary"
                  type="submit"
                  disabled={createBusy}
                >
                  {createBusy
                    ? t("testsModule.dashboard.creating", {
                        defaultValue: "Creating...",
                      })
                    : t("testsModule.dashboard.create", {
                        defaultValue: "Create",
                      })}
                </button>
                {createdTestId ? (
                  <Link
                    className="btn ghost"
                    to={`/tests/${createdTestId}/edit`}
                  >
                    {t("testsModule.editor.editTestTitle", {
                      defaultValue: "Edit test",
                    })}
                  </Link>
                ) : null}
              </div>
            </div>
          </form>
        </section>

        {createdTestId ? (
          <ProgramTestEditor overrideTestId={createdTestId} embedded />
        ) : null}
      </div>
    </main>
  );
}
